/**
 * Pieces of the Cup O' Joe podcast landing hero: the platform badge links
 * ({@link SocialPlatformLinks}) and the hero itself ({@link PodcastHeroSection}), with its
 * "Register for the Live Show!" button.
 *
 * Used only by `./Podcasts.tsx`. Copy and the platform URLs come from `./Podcast.content.tsx`
 * (the live-show URL is {@link cloudRegistrationUrl}, below); styles from
 * `./Podcast.styles.tsx`. The badge images are the same files `./PodcastDetail.tsx` hard-codes
 * for each episode's links, under
 * `public/images/metal-resources/metal-market-intelligence/podcast/`.
 */
import React from "react";
import { Link } from "@ryerson/frontend.navigation";
import { Flex, ContentSection } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { Button } from "@ryerson/frontend.button";
import * as PodcastStyles from "./Podcast.styles";

type PodcastPlatform = "apple" | "spotify" | "amazon" | "youtube";

interface SocialPlatform {
	name: PodcastPlatform;
	url: string;
	alt: string;
	imagePath: string;
	/** The last badge has no right margin. */
	isLast?: boolean;
	/** Extra styles on the badge's link. */
	customStyles?: any;
}

interface SocialPlatformLinksProps {
	/** The show's URL on each platform. */
	staticContent: {
		apple: string;
		spotify: string;
		amazon: string;
		youtube: string;
	};
	/** `"vertical"` stacks the badges in a column. Both callers pass `"horizontal"`. */
	layout?: "horizontal" | "vertical";
	/** `false` nudges the YouTube badge 4px down. */
	isMobile?: boolean;
}

/**
 * Zoom webinar registration page for the live show, opened in a new tab by the hero's
 * "Register for the Live Show!" button. It is a literal here, not Contentful content, so a new
 * webinar needs a code change and a release.
 */
export const cloudRegistrationUrl =
	"https://jtryerson.zoom.us/webinar/register/WN_EqKyxlkeQZ2TT-N4lOHuKg";

// Helper function to generate podcast platform image paths
/** Site-relative path of a platform's badge image. */
export const getPodcastImagePath = (platform: PodcastPlatform): string => {
	const basePath = "/images/metal-resources/metal-market-intelligence/podcast";
	const platformMap: { [key in PodcastPlatform]: string } = {
		apple: "ApplePodcast.png",
		spotify: "SpotifyPodcast.png",
		amazon: "AmazonPodcast.png",
		youtube: "YoutubePodcast.png",
	};
	return `${basePath}/${platformMap[platform]}`;
};

/**
 * The four platform badges (Apple Podcasts, Spotify, Amazon Music, YouTube), in that order, each
 * linking to the show's page in a new tab as a plain anchor (`next={false}`).
 */
export const SocialPlatformLinks: React.FC<SocialPlatformLinksProps> = ({
	staticContent,
	layout = "horizontal",
	isMobile = false,
}) => {
	const platforms: SocialPlatform[] = [
		{
			name: "apple",
			url: staticContent.apple,
			alt: "Apple Podcast",
			imagePath: getPodcastImagePath("apple"),
		},
		{
			name: "spotify",
			url: staticContent.spotify,
			alt: "Spotify",
			imagePath: getPodcastImagePath("spotify"),
		},
		{
			name: "amazon",
			url: staticContent.amazon,
			alt: "Amazon Music",
			imagePath: getPodcastImagePath("amazon"),
		},
		{
			name: "youtube",
			url: staticContent.youtube,
			alt: "YouTube",
			imagePath: getPodcastImagePath("youtube"),
			isLast: true,
			customStyles: !isMobile ? { position: "relative", top: 4 } : undefined,
		},
	];

	if (layout === "vertical") {
		return (
			<Flex direction="column" css={PodcastStyles.PlatformLinksContainerCSS}>
				{platforms.map((platform) => (
					<Link
						key={platform.name}
						to={platform.url}
						target="_blank"
						next={false}
						css={platform.customStyles}
					>
						<img
							src={platform.imagePath}
							alt={platform.alt}
							css={
								platform.isLast
									? PodcastStyles.PlatformLinkLastCSS
									: PodcastStyles.PlatformLinkCSS
							}
						/>
					</Link>
				))}
			</Flex>
		);
	}

	return (
		<div css={PodcastStyles.PlatformLinksContainerCSS}>
			{platforms.map((platform) => (
				<Link
					key={platform.name}
					to={platform.url}
					target="_blank"
					next={false}
					css={platform.customStyles}
				>
					<img
						src={platform.imagePath}
						alt={platform.alt}
						css={
							platform.isLast
								? PodcastStyles.PlatformLinkLastCSS
								: PodcastStyles.PlatformLinkCSS
						}
					/>
				</Link>
			))}
		</div>
	);
};

interface PodcastHeroSectionProps {
	/** One language's `./Podcast.content.tsx` → `podcastStaticContent` entry. */
	staticContent: any;
	theme: any;
	/** Desktop hero height (CSS length). Ignored on mobile, which is fixed at 740px. */
	heroHeight: string;
	/** Desktop `ContentSection` vertical padding: half of `heroHeight`. Ignored on mobile. */
	vPadding: string;
	isMobile?: boolean;
}

/**
 * The podcast landing hero: background photo under a blue gradient, breadcrumb, title, blurb,
 * platform badges and the live-show registration button. Desktop shows the logo top right;
 * mobile shows it below the button.
 *
 * The photo and overlay are absolutely positioned, so the surrounding `ContentSection`'s padding
 * (`vPadding` on desktop, 370px on mobile) is what reserves their height.
 */
export const PodcastHeroSection: React.FC<PodcastHeroSectionProps> = ({
	staticContent,
	theme,
	heroHeight,
	vPadding,
	isMobile = false,
}) => {
	const { HeroContainer, HeroImage, BreadcrumbContainer, PoweredBy } = PodcastStyles;

	return (
		<ContentSection vPadding={isMobile ? "370px" : vPadding} type="tertiary">
			<HeroImage
				css={
					isMobile
						? PodcastStyles.HeroImageMobileCSS(staticContent.backgroundImage)
						: PodcastStyles.HeroImageDesktopCSS(
								staticContent.backgroundImage,
								heroHeight
						  )
				}
			/>
			{isMobile ? (
				<div css={PodcastStyles.HeroOverlayMobileCSS}>
					<div css={PodcastStyles.HeroContentMobileCSS}>
						<BreadcrumbContainer
							theme={theme}
							css={PodcastStyles.BreadcrumbContainerMobileCSS}
						>
							<Breadcrumb type="tertiary" size="xs" />
						</BreadcrumbContainer>
						<HeroContainer css={PodcastStyles.HeroContainerMobileCSS}>
							<Typography
								variant="h1"
								type="tertiary"
								css={PodcastStyles.HeroTitleMobileCSS}
								weight="bolder"
							>
								{staticContent.title}
							</Typography>
							<Typography
								variant="div"
								css={PodcastStyles.HeroBlurbMobileCSS}
								color={theme.colors.tertiary.header}
								size="sm"
							>
								{staticContent.blurb}
							</Typography>
						</HeroContainer>
						<Flex direction="column" className="links-display" css={{ gap: "20px" }}>
							<Typography
								variant="div"
								size="sm"
								type="tertiary"
								color={theme.colors.primary.white}
							>
								{staticContent.followUs}
							</Typography>
							<SocialPlatformLinks
								staticContent={staticContent}
								layout="horizontal"
								isMobile={true}
							/>
							<Button
								size="xl"
								type="primary"
								fullwidth={true}
								label={staticContent.registerForLiveShow}
								onClick={() => window.open(cloudRegistrationUrl, "_blank")}
							/>
						</Flex>
						<img
							src={staticContent.imageUrl}
							css={PodcastStyles.PodcastLogoMobileCSS}
						/>
					</div>
				</div>
			) : (
				<Flex css={PodcastStyles.HeroOverlayDesktopCSS(heroHeight)}>
					<div css={PodcastStyles.HeroContentDesktopCSS}>
						<BreadcrumbContainer
							theme={theme}
							css={PodcastStyles.BreadcrumbContainerDesktopCSS}
						>
							<Breadcrumb type="tertiary" size="xs" />
						</BreadcrumbContainer>
						<HeroContainer css={PodcastStyles.HeroContainerDesktopCSS}>
							<PoweredBy>
								<img src={staticContent.imageUrl} alt={staticContent.title} />
							</PoweredBy>
							<Typography
								variant="h1"
								type="tertiary"
								css={PodcastStyles.HeroTitleDesktopCSS(theme.breakpoints.xl)}
								weight="bolder"
							>
								{staticContent.title}
							</Typography>
							<Typography
								variant="div"
								css={PodcastStyles.HeroBlurbDesktopCSS}
								color={theme.colors.tertiary.header}
								size="sm"
							>
								{staticContent.blurb}
							</Typography>
						</HeroContainer>
						<Typography
							variant="div"
							size="sm"
							type="tertiary"
							color={theme.colors.primary.white}
						>
							{staticContent.followUs}
						</Typography>
						<Flex
							className="links-display"
							css={{ gap: "120px", alignItems: "end", alignContent: "center" }}
						>
							<SocialPlatformLinks
								staticContent={staticContent}
								layout="horizontal"
								isMobile={false}
							/>
							<Button
								size="md"
								type="primary"
								label={staticContent.registerForLiveShow}
								onClick={() => window.open(cloudRegistrationUrl, "_blank")}
							/>
						</Flex>
					</div>
				</Flex>
			)}
		</ContentSection>
	);
};
